import { useEffect, useRef } from 'react';
import { io, Socket } from 'socket.io-client';
import { SOCKET_URL } from '../constants/apiConfig';

// Global socket instance to maintain persistent connection across all components
let globalSocket: Socket | null = null;
const connectedJobIds = new Set<string>();
const componentCallbacks = new Map<string, any[]>();
let componentIdCounter = 0;

interface UseGlobalSocketOptions {
  enabled?: boolean;
  jobId?: string;
  onCommentCreated?: (comment: any) => void;
  onCommentReplyCreated?: (reply: any) => void;
  onCommentPinUpdated?: (comment: any) => void;
  onCommentDeleted?: (data: { commentId: string; jobId: string }) => void;
  onTaskCreated?: (task: any) => void;
  onTaskUpdated?: (task: any) => void;
}

export function useGlobalSocket({
  enabled = false,
  jobId,
  onCommentCreated,
  onCommentReplyCreated,
  onCommentPinUpdated,
  onCommentDeleted,
  onTaskCreated,
  onTaskUpdated
}: UseGlobalSocketOptions) {
  const componentIdRef = useRef<string>(
    `component-${++componentIdCounter}-${Date.now()}`
  );

  const callbacksRef = useRef({
    componentId: componentIdRef.current,
    onCommentCreated,
    onCommentReplyCreated,
    onCommentPinUpdated,
    onCommentDeleted,
    onTaskCreated,
    onTaskUpdated
  });

  useEffect(() => {
    callbacksRef.current = {
      componentId: componentIdRef.current,
      onCommentCreated,
      onCommentReplyCreated,
      onCommentPinUpdated,
      onCommentDeleted,
      onTaskCreated,
      onTaskUpdated
    };

    if (jobId && componentCallbacks.has(jobId)) {
      const callbacks = componentCallbacks.get(jobId) || [];
      const existingIndex = callbacks.findIndex(
        cb => cb.componentId === componentIdRef.current
      );
      if (existingIndex !== -1) {
        callbacks[existingIndex] = callbacksRef.current;
        // console.log(
        //   `🔄 Updated callback for component ${componentIdRef.current} on job ${jobId}`
        // );
      } else {
        const hasStaleCallbacks = callbacks.length > 1;
        if (hasStaleCallbacks) {
          const latestCallback = callbacks[callbacks.length - 1];
          callbacks.length = 0;
          callbacks.push(latestCallback, callbacksRef.current);
          // console.log(
          //   `🔄 Cleaned ${hasStaleCallbacks} stale callbacks for job ${jobId}`
          // );
        } else {
          callbacks.push(callbacksRef.current);
        }
      }
      componentCallbacks.set(jobId, callbacks);
    }
  }, [
    onCommentCreated,
    onCommentReplyCreated,
    onCommentPinUpdated,
    onCommentDeleted,
    onTaskCreated,
    onTaskUpdated,
    jobId
  ]);

  useEffect(() => {
    if (!enabled) return;

    if (!globalSocket || !globalSocket.connected) {
      console.log('Creating new global socket connection...');

      const isProduction = !SOCKET_URL.includes('localhost');

      globalSocket = io(SOCKET_URL, {
        transports: isProduction
          ? ['polling', 'websocket']
          : ['websocket', 'polling'],
        withCredentials: true,
        reconnection: true,
        reconnectionAttempts: 15,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 8000,
        timeout: 45000,
        forceNew: false,
        upgrade: true,
        rememberUpgrade: true,
        ...(isProduction && {
          pingTimeout: 60000,
          pingInterval: 25000,
          maxHttpBufferSize: 1e6
        })
      });

      // console.log('🔌 Creating socket connection:', {
      //   url: SOCKET_URL,
      //   isProduction,
      //   transports: globalSocket.io.opts.transports
      // });

      const handleConnect = () => {
        connectedJobIds.forEach(jobId => {
          globalSocket?.emit('joinJobRoom', { jobId });
        });
      };

      const handleReconnect = () => {
        connectedJobIds.forEach(jobId => {
          globalSocket?.emit('joinJobRoom', { jobId });
        });
      };

      const handleCommentCreated = (eventData: any) => {
        // console.log('🔥 Comment created event received globally:', eventData);
        const commentData = eventData.data || eventData;

        if (commentData.jobId && componentCallbacks.has(commentData.jobId)) {
          const callbacks = componentCallbacks.get(commentData.jobId) || [];
          callbacks.forEach(callback => {
            if (callback.onCommentCreated) {
              callback.onCommentCreated(commentData);
            }
          });
        }
      };

      const handleCommentReplyCreated = (eventData: any) => {
        // console.log('💬 Reply created event received globally:', eventData);
        const replyData = eventData.data || eventData;

        // console.log('💬 Reply data:', {
        //   jobId: replyData.jobId,
        //   replyId: replyData._id,
        //   parentCommentId: replyData.parentCommentId,
        //   content: replyData.content?.substring(0, 50) + '...',
        //   hasCallbacks: componentCallbacks.has(replyData.jobId),
        //   callbackCount: componentCallbacks.get(replyData.jobId)?.length || 0
        // });

        if (replyData.jobId && componentCallbacks.has(replyData.jobId)) {
          const callbacks = componentCallbacks.get(replyData.jobId) || [];
          let processedCount = 0;

          callbacks.forEach(callback => {
            if (callback.onCommentReplyCreated) {
              try {
                callback.onCommentReplyCreated(replyData);
                processedCount++;
              } catch (error) {
                console.error('❌ Error processing reply callback:', error);
              }
            }
          });

          console.log(`✅ Processed reply for ${processedCount} components`);
        } else {
          console.warn('⚠️ No callbacks found for job:', replyData.jobId);
        }
      };

      const handleCommentPinUpdated = (eventData: any) => {
        console.log('Comment pin updated event received globally:', eventData);
        const commentData = eventData.data || eventData;

        if (commentData.jobId && componentCallbacks.has(commentData.jobId)) {
          const callbacks = componentCallbacks.get(commentData.jobId) || [];
          callbacks.forEach(callback => {
            if (callback.onCommentPinUpdated) {
              callback.onCommentPinUpdated(commentData);
            }
          });
        }
      };

      const handleCommentDeleted = (eventData: any) => {
        console.log('Comment deleted event received globally:', eventData);
        const deleteData = eventData.data || eventData;

        if (deleteData.jobId && componentCallbacks.has(deleteData.jobId)) {
          const callbacks = componentCallbacks.get(deleteData.jobId) || [];
          callbacks.forEach(callback => {
            if (callback.onCommentDeleted) {
              callback.onCommentDeleted(deleteData);
            }
          });
        }
      };

      const handleTaskCreated = (taskData: any) => {
        console.log('Task created event received globally:', taskData);

        if (taskData.jobId && componentCallbacks.has(taskData.jobId)) {
          const callbacks = componentCallbacks.get(taskData.jobId) || [];
          callbacks.forEach(callback => {
            if (callback.onTaskCreated) {
              callback.onTaskCreated(taskData);
            }
          });
        }
      };

      const handleTaskUpdated = (taskData: any) => {
        console.log('Task updated event received globally:', taskData);

        if (taskData.jobId && componentCallbacks.has(taskData.jobId)) {
          const callbacks = componentCallbacks.get(taskData.jobId) || [];
          callbacks.forEach(callback => {
            if (callback.onTaskUpdated) {
              callback.onTaskUpdated(taskData);
            }
          });
        }
      };

      const handleDisconnect = (reason: string) => {
        if (reason === 'transport error' || reason === 'transport close') {
          setTimeout(() => {
            if (globalSocket && !globalSocket.connected) {
              globalSocket.connect();
            }
          }, 1000);
        }
      };

      const handleConnectError = (error: any) => {
        console.error('❌ Global socket connection error:', {
          error: error.message || error,
          url: SOCKET_URL,
          transport: globalSocket?.io?.engine?.transport?.name,
          readyState: globalSocket?.io?.engine?.readyState
        });
      };

      const handleReconnectError = (error: any) => {
        console.error('❌ Global socket reconnection error:', error);
      };

      const handleReconnectFailed = () => {
        console.error(
          '❌ Global socket reconnection failed after all attempts'
        );
      };

      globalSocket.on('connect', handleConnect);
      globalSocket.on('reconnect', handleReconnect);
      globalSocket.on('reconnect_error', handleReconnectError);
      globalSocket.on('reconnect_failed', handleReconnectFailed);
      globalSocket.on('commentCreated', handleCommentCreated);
      globalSocket.on('commentReplyCreated', handleCommentReplyCreated);
      globalSocket.on('commentPinUpdated', handleCommentPinUpdated);
      globalSocket.on('commentDeleted', handleCommentDeleted);
      globalSocket.on('taskCreated', handleTaskCreated);
      globalSocket.on('taskUpdated', handleTaskUpdated);
      globalSocket.on('disconnect', handleDisconnect);
      globalSocket.on('connect_error', handleConnectError);
    }

    // Register this component's callbacks for the job
    if (jobId) {
      if (!componentCallbacks.has(jobId)) {
        componentCallbacks.set(jobId, []);
      }

      const callbacks = componentCallbacks.get(jobId) || [];

      // Check if this component is already registered
      const existingIndex = callbacks.findIndex(
        cb => cb.componentId === componentIdRef.current
      );
      if (existingIndex === -1) {
        // Only add if not already registered
        callbacks.push(callbacksRef.current);
        componentCallbacks.set(jobId, callbacks);
        // console.log(
        //   `➕ Registered component ${componentIdRef.current} for job ${jobId} (Total: ${callbacks.length})`
        // );
      } else {
        // Update existing callback to ensure it's fresh
        callbacks[existingIndex] = callbacksRef.current;
        componentCallbacks.set(jobId, callbacks);
        // console.log(
        //   `🔄 Updated existing registration for component ${componentIdRef.current} on job ${jobId}`
        // );
      }

      // Join job room if not already joined
      if (!connectedJobIds.has(jobId)) {
        if (globalSocket.connected) {
          // console.log(
          //   `🏠 Joining job room: ${jobId} (Component: ${componentIdRef.current})`
          // );
          globalSocket.emit('joinJobRoom', { jobId });
          connectedJobIds.add(jobId);
        } else {
          // console.log(
          //   `⏳ Queuing job room for connection: ${jobId} (Component: ${componentIdRef.current})`
          // );
          connectedJobIds.add(jobId);
        }
      } else {
        // console.log(
        //   `✅ Already connected to job room: ${jobId} (Component: ${componentIdRef.current})`
        // );
      }
    }

    // Cleanup function - remove this component's callbacks
    return () => {
      if (jobId && componentCallbacks.has(jobId)) {
        const callbacks = componentCallbacks.get(jobId) || [];
        const filteredCallbacks = callbacks.filter(
          cb => cb.componentId !== componentIdRef.current
        );

        // console.log(
        //   `🧹 Cleanup for component ${componentIdRef.current} on job ${jobId} (Remaining: ${filteredCallbacks.length})`
        // );

        if (filteredCallbacks.length === 0) {
          componentCallbacks.delete(jobId);

          // Leave job room if no more components are listening
          if (globalSocket && globalSocket.connected) {
            globalSocket.emit('leaveJobRoom', { jobId });
            // console.log(
            //   `🚪 Leaving job room: ${jobId} (no more active components)`
            // );
            connectedJobIds.delete(jobId);
          }
        } else {
          componentCallbacks.set(jobId, filteredCallbacks);
        }
      }
    };
  }, [enabled, jobId]);

  useEffect(() => {
    return () => {
      if (componentCallbacks.size === 0 && globalSocket) {
        // console.log('No more components using socket, disconnecting...');
        globalSocket.disconnect();
        globalSocket = null;
      }
    };
  }, []);

  return globalSocket;
}
